const EARTH_RADIUS_M = 6371000;
const toRad = (deg) => (deg * Math.PI) / 180;

export const haversineMeters = (lat1, lng1, lat2, lng2) => {
  const dLat = toRad(lat2 - lat1);
  const dLng = toRad(lng2 - lng1);
  const a =
    Math.sin(dLat / 2) ** 2 +
    Math.cos(toRad(lat1)) * Math.cos(toRad(lat2)) * Math.sin(dLng / 2) ** 2;
  return 2 * EARTH_RADIUS_M * Math.asin(Math.sqrt(a));
};

export const pointInRectangle = (lat, lng, ne, sw) => {
  if (!ne || !sw) return false;
  const inLat = lat <= ne.Lat && lat >= sw.Lat;
  const minLng = Math.min(sw.Lng, ne.Lng);
  const maxLng = Math.max(sw.Lng, ne.Lng);
  const inLng = lng >= minLng && lng <= maxLng;
  return inLat && inLng;
};

export const pointInPolygon = (lat, lng, points) => {
  if (!Array.isArray(points) || points.length < 3) return false;
  let inside = false;
  for (let i = 0, j = points.length - 1; i < points.length; j = i++) {
    const xi = points[i].Lng, yi = points[i].Lat;
    const xj = points[j].Lng, yj = points[j].Lat;
    const intersect =
      yi > lat !== yj > lat &&
      lng < ((xj - xi) * (lat - yi)) / (yj - yi + 1e-12) + xi;
    if (intersect) inside = !inside;
  }
  return inside;
};

const orientation = (p, q, r) => {
  const val = (q.Lat - p.Lat) * (r.Lng - q.Lng) - (q.Lng - p.Lng) * (r.Lat - q.Lat);
  if (Math.abs(val) < 1e-12) return 0;
  return val > 0 ? 1 : 2;
};

const onSegment = (p, q, r) =>
  q.Lng <= Math.max(p.Lng, r.Lng) &&
  q.Lng >= Math.min(p.Lng, r.Lng) &&
  q.Lat <= Math.max(p.Lat, r.Lat) &&
  q.Lat >= Math.min(p.Lat, r.Lat);

const segmentsIntersect = (p1, q1, p2, q2) => {
  const o1 = orientation(p1, q1, p2);
  const o2 = orientation(p1, q1, q2);
  const o3 = orientation(p2, q2, p1);
  const o4 = orientation(p2, q2, q1);
  if (o1 !== o2 && o3 !== o4) return true;
  if (o1 === 0 && onSegment(p1, p2, q1)) return true;
  if (o2 === 0 && onSegment(p1, q2, q1)) return true;
  if (o3 === 0 && onSegment(p2, p1, q2)) return true;
  if (o4 === 0 && onSegment(p2, q1, q2)) return true;
  return false;
};

export const isSelfIntersecting = (points) => {
  if (!Array.isArray(points) || points.length < 4) return false;
  const n = points.length;
  for (let i = 0; i < n; i++) {
    const p1 = points[i], q1 = points[(i + 1) % n];
    for (let j = i + 1; j < n; j++) {
      if (j === i + 1 || (i === 0 && j === n - 1)) continue;
      const p2 = points[j], q2 = points[(j + 1) % n];
      if (segmentsIntersect(p1, q1, p2, q2)) return true;
    }
  }
  return false;
};

export const isInsideFence = (lat, lng, fence) => {
  if (!fence) return false;
  if (fence.GeofenceType === 'CircularFixed200') {
    if (!fence.Center) return false;
    return haversineMeters(lat, lng, fence.Center.Lat, fence.Center.Lng) <= 200;
  }
  if (fence.GeofenceType === 'CircularCustom') {
    if (!fence.Center || !fence.RadiusMeters) return false;
    return haversineMeters(lat, lng, fence.Center.Lat, fence.Center.Lng) <= fence.RadiusMeters;
  }
  if (fence.GeofenceType === 'RectangleCustom') {
    return pointInRectangle(lat, lng, fence.RectangleBounds?.NE, fence.RectangleBounds?.SW);
  }
  if (fence.GeofenceType === 'PolygonCustom') {
    return pointInPolygon(lat, lng, fence.PolygonPoints || []);
  }
  return false;
};
